import {
  BellRingIcon,
  BotIcon,
  CakeIcon,
  CalendarCheckIcon,
  CalendarDaysIcon,
  ClockIcon,
  FileSignatureIcon,
  FileTextIcon,
  FilterIcon,
  GaugeIcon,
  GiftIcon,
  HandCoinsIcon,
  HeartHandshakeIcon,
  InboxIcon,
  KanbanSquareIcon,
  LayoutTemplateIcon,
  ListChecksIcon,
  MailIcon,
  MegaphoneIcon,
  MessageCircleIcon,
  MessageSquareReplyIcon,
  MessagesSquareIcon,
  NewspaperIcon,
  PhoneForwardedIcon,
  PhoneMissedIcon,
  QrCodeIcon,
  ReceiptIcon,
  RefreshCwIcon,
  Share2Icon,
  SmartphoneIcon,
  SmartphoneNfcIcon,
  SparklesIcon,
  StarIcon,
  SunIcon,
  TargetIcon,
  UsersIcon,
  VideoIcon,
  VoicemailIcon,
  type LucideIcon,
} from 'lucide-react'

// Link placeholders — swap these for the real destinations.
export const crmLinks = {
  seeItInAction: '#/contact',
  talkToUs: '#/contact',
  bookWalkthrough: '#/contact',
}

export type CrmFeature = {
  name: string
  outcome: string
  icon: LucideIcon
}

export type JourneyStage = {
  id: 'capture' | 'nurture' | 'close' | 'evangelize' | 'reactivate'
  label: string
  headline: string
  sub: string
  features: CrmFeature[]
}

export const problems = [
  {
    title: 'Missed Calls Go To Competitors',
    body: 'Most callers won’t leave a voicemail. They just call the next business on the list.',
    icon: PhoneMissedIcon,
  },
  {
    title: 'Leads Go Cold In Hours',
    body: 'Slow replies lose jobs you already paid to get.',
    icon: ClockIcon,
  },
  {
    title: 'Past Customers Forget You',
    body: 'Your old customer list is money sitting on the table.',
    icon: UsersIcon,
  },
]

export const journeyStages: JourneyStage[] = [
  {
    id: 'capture',
    label: 'Capture',
    headline: 'Get more leads in the door',
    sub: 'Every call, form, chat and message lands in one place — nothing slips through.',
    features: [
      { name: 'Missed-Call Text-Back', outcome: 'Texts callers back automatically so they don’t call your competitor.', icon: PhoneMissedIcon },
      { name: 'AI Phone Answering', outcome: 'Picks up 24/7, answers questions and books the appointment.', icon: BotIcon },
      { name: 'Website Chat Widget', outcome: 'Turns visitors into conversations, even after hours.', icon: MessageCircleIcon },
      { name: 'Forms, Surveys & Quizzes', outcome: 'Capture details and qualify leads up front.', icon: ListChecksIcon },
      { name: 'Landing Pages & Funnels', outcome: 'Pages built to convert your ad traffic.', icon: LayoutTemplateIcon },
      { name: 'Call Tracking', outcome: 'See which ad or channel made the phone ring.', icon: PhoneForwardedIcon },
      { name: 'Social DMs Captured', outcome: 'Facebook and Instagram messages become leads automatically.', icon: MessagesSquareIcon },
      { name: 'Ad Leads Flow Straight In', outcome: 'Google and Facebook leads drop into the CRM instantly.', icon: TargetIcon },
      { name: 'QR Codes & Business-Card Scanner', outcome: 'Turn events and in-person meetings into contacts.', icon: QrCodeIcon },
    ],
  },
  {
    id: 'nurture',
    label: 'Nurture',
    headline: 'Follow up fast, without lifting a finger',
    sub: 'The speed and consistency that wins the job — on autopilot.',
    features: [
      { name: 'One Inbox', outcome: 'Text, email, Facebook, Instagram, WhatsApp and web chat in one feed.', icon: InboxIcon },
      { name: 'AI Replies 24/7', outcome: 'Answers common questions and books appointments while you sleep.', icon: SparklesIcon },
      { name: 'Automated Follow-Up', outcome: 'Text and email sequences that run until the lead responds.', icon: RefreshCwIcon },
      { name: 'Online Booking & Calendars', outcome: 'Let customers book themselves. Syncs with your calendar.', icon: CalendarDaysIcon },
      { name: 'Appointment Reminders', outcome: 'Fewer no-shows with automatic text and email reminders.', icon: BellRingIcon },
      { name: 'Ringless Voicemail', outcome: 'Drop a voicemail without the phone ringing.', icon: VoicemailIcon },
      { name: 'Sales Pipeline Board', outcome: 'See every deal and exactly where it stands.', icon: KanbanSquareIcon },
      { name: 'Mobile App', outcome: 'Reply, call and send video messages from your phone.', icon: SmartphoneIcon },
    ],
  },
  {
    id: 'close',
    label: 'Close',
    headline: 'Close deals with less back-and-forth',
    sub: 'Quote it, sign it, get paid — all in one place.',
    features: [
      { name: 'Lead Scoring', outcome: 'Know who’s ready to buy and call them first.', icon: GaugeIcon },
      { name: 'Estimates & Proposals', outcome: 'Send professional quotes in minutes.', icon: FileSignatureIcon },
      { name: 'Invoices', outcome: 'Bill and track payments without another app.', icon: ReceiptIcon },
      { name: 'Text-To-Pay', outcome: 'Send a payment link by text and get paid in seconds.', icon: MessageSquareReplyIcon },
      { name: 'Tap-To-Pay', outcome: 'Take card payments on your phone in the field.', icon: SmartphoneNfcIcon },
      { name: 'Deposits At Booking', outcome: 'Collect upfront to lock in the appointment.', icon: HandCoinsIcon },
      { name: 'Gift Cards', outcome: 'Sell them online, year-round.', icon: GiftIcon },
    ],
  },
  {
    id: 'evangelize',
    label: 'Evangelize',
    headline: 'Turn happy customers into your sales team',
    sub: 'More five-star reviews, more referrals, more word of mouth.',
    features: [
      { name: 'Automatic Review Requests', outcome: 'Ask every happy customer at the perfect moment.', icon: StarIcon },
      { name: 'AI Review Replies', outcome: 'Every review gets a thoughtful response automatically.', icon: MessageSquareReplyIcon },
      { name: 'Review Widgets', outcome: 'Show your best reviews on your website.', icon: LayoutTemplateIcon },
      { name: 'Video Testimonials', outcome: 'Customers record a video review from their phone.', icon: VideoIcon },
      { name: 'Referral Tracking', outcome: 'Reward customers who send you business.', icon: HeartHandshakeIcon },
      { name: 'Loyalty Program', outcome: 'Give customers a reason to come back.', icon: GiftIcon },
      { name: 'Auto-Share 5-Star Reviews', outcome: 'Great reviews post to your social media automatically.', icon: Share2Icon },
    ],
  },
  {
    id: 'reactivate',
    label: 'Reactivate',
    headline: 'Wake up the customers you already have',
    sub: 'Your past customer list is your fastest, cheapest source of new revenue.',
    features: [
      { name: 'Broadcast Campaigns', outcome: 'One message to your whole list by text, email or WhatsApp.', icon: MegaphoneIcon },
      { name: 'Smart Lists', outcome: 'Target the right people by last visit, service type or spend.', icon: FilterIcon },
      { name: 'Birthday & Anniversary Campaigns', outcome: 'Personal touches that bring people back.', icon: CakeIcon },
      { name: 'Seasonal Promotions', outcome: 'Scheduled offers for your busy and slow seasons.', icon: SunIcon },
      { name: 'Dead-Lead Reactivation', outcome: 'Proven campaigns that revive old leads.', icon: RefreshCwIcon },
      { name: 'Newsletters', outcome: 'Stay top of mind every month.', icon: NewspaperIcon },
      { name: 'AI-Written Content', outcome: 'Campaign messages drafted for you.', icon: SparklesIcon },
    ],
  },
]

export const scatteredApps = [
  { label: 'Scheduling App', icon: CalendarCheckIcon },
  { label: 'Texting App', icon: MessageCircleIcon },
  { label: 'Email Tool', icon: MailIcon },
  { label: 'Review Tool', icon: StarIcon },
  { label: 'Invoicing Tool', icon: FileTextIcon },
  { label: 'Spreadsheet', icon: KanbanSquareIcon },
]

export const whyAdroi = [
  {
    title: 'Proven platform, built for you',
    body: '1Dash.Pro is Powered by HighLevel, and our team does the setup, automations, pipelines and templates — not a login and a good-luck.',
  },
  {
    title: 'Connected To Your Ads',
    body: 'We see which ads bring in real customers — not just clicks.',
  },
  {
    title: 'Real People, Real Support',
    body: 'A team that knows your business and is there when you need help.',
  },
]

export const mobilePoints = ['Reply to leads anywhere.', 'See today’s appointments.', 'Get paid on the spot.']
